import React, { useCallback, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ERMSDashboard from '../components/requisition/ERMSDashboard';
import NewRequisition from '../components/requisition/NewRequisition';
import MyRequisitions from '../components/requisition/MyRequisitions';
import DepartmentRequisitions from '../components/requisition/DepartmentRequisitions';
import MyInbox from '../components/requisition/MyInbox';
import ApprovalOutcomeRequisitions from '../components/requisition/ApprovalOutcomeRequisitions';
import * as api from '../api';
import { RNLLoader } from '../components/UI';

const CC_WORKSPACES = ['load', 'reviewed', 'pending', 'rejected'];
const FORM_VIEWS = ['form', 'review', 'drafts'];

export default function EnterpriseRequisitionPage() {
  // ?tab= lets another page send the reader back to the tab they came from —
  // the edit screen's "Back to Approved Requisitions" is the reason it exists.
  // Read once, for the initial tab only: clicking the pills after that is
  // ordinary state and does not rewrite the URL.
  const navigate = useNavigate();
  const params = useParams();
  const tabFromPath = params.tab;
  const [searchParams] = useSearchParams();

  // The tab IS the URL now — no local state to drift out of step with it.
  // Every pill, and every programmatic jump below, goes through goToTab.
  //
  // ?tab= is still honoured so links minted before this change keep working:
  // the edit page's Back button emits them, and anything a user bookmarked
  // would otherwise land on the dashboard with no explanation.
  const activeTab = tabFromPath || searchParams.get('tab') || 'dashboard';

  // Everything after the tab, e.g. ["load"] for
  // /enterprise-requisition/cc-queue/load. Each tab decides what its own
  // segments mean; the page only splits them.
  const subPath = (params['*'] || '').split('/').filter(Boolean);

  // A requisition id looks like a uuid; anything else in that slot is a view
  // name. Checked rather than assumed so /cc-queue/load and /cc-queue/<uuid>
  // can share one position without ambiguity.
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const openPrFrom = (segments) => {
    const id = segments.find((seg) => UUID.test(seg)) || null;
    return { id, edit: id ? segments[segments.indexOf(id) + 1] === 'edit' : false };
  };
  const opened = openPrFrom(subPath);

  // Where a list sends the browser when a row is opened or closed. The list
  // keeps no opinion about its own address — it says what happened and this
  // decides the url.
  const prNav = (base) => (id, edit) => navigate(
    id ? `${base}/${id}${edit ? '/edit' : ''}` : base,
  );

  const goToTab = useCallback((id) => {
    // Each tab re-mounts its component fresh, but the GET response cache in
    // api.js would otherwise still serve stale data for repeat visits to the
    // same endpoint — bust it on every switch so the tab reflects the latest
    // state.
    api.clearApiCache();
    navigate(`/enterprise-requisition/${id}`);
  }, [navigate]);
  const [currentUser, setCurrentUser] = useState(null);

  // Fetch current user to determine roles
  React.useEffect(() => {
    const fetchMe = async () => {
      try {
        const me = await api.getMe();
        setCurrentUser(me);
      } catch (e) {
        console.error("Failed to fetch user", e);
      }
    };
    fetchMe();
  }, []);

  const userRoleNames = (currentUser?.roles || []).map(r => 
    typeof r === 'string' ? r.toLowerCase() : (r.role_name || r.name || '').toLowerCase()
  );

  const isSuperAdmin = userRoleNames.some(role => role === 'super_admin');

  // CC Queue: visible ONLY to users with cost_control role
  const canSeeCcQueue = userRoleNames.some(role => role === 'cost_control');

  // Approval Queue: visible ONLY to users with md or dmd roles
  const canSeeApprovalQueue = userRoleNames.some(role => role === 'md' || role === 'dmd');

  // Department Head Review: visible ONLY to users with the department_head role
  const canSeeDepartmentHeadReview = userRoleNames.some(role => role === 'department_head');

  // Department Requisitions: open to everyone — the backend scopes the data to
  // MD/DMD and all_requisitions (every department), department_head (the
  // department(s) they head), and everyone else (only the department(s) they
  // belong to). The same applies to My Requisitions, which is unconditional
  // here and widened server-side for those same breadth roles.
  const canSeeDepartmentRequisitions = true;

  const handleCreateNew = () => {
    goToTab('new');
  };

  const navItems = [
    { id: 'dashboard', label: 'ERMS Dashboard' },
    { id: 'new', label: 'New Requisition' },
    { id: 'my-requisitions', label: 'My Requisitions' },
    // { id: 'my-inbox', label: 'My Inbox' },
  ];

  if (canSeeDepartmentRequisitions) {
    navItems.push({ id: 'department', label: 'Department Requisitions' });
  }

  // if (canSeeDepartmentHeadReview) {
  //   navItems.push({ id: 'department-head-review', label: 'Department Head Review' });
  // }

  if (canSeeCcQueue) {
    navItems.push({ id: 'cc-queue', label: 'Cost Control Queue' });
  }

  if (canSeeApprovalQueue) {
    navItems.push({ id: 'approval-queue', label: 'Approval Queue' });
    navItems.push({ id: 'approved-requisitions', label: 'Approved Requisitions' });
    navItems.push({ id: 'rejected-returned-requisitions', label: 'Rejected/Returned Requisitions' });
  }

  // navItems is built from roles that arrive asynchronously, so on the first
  // paint it holds only the tabs everyone gets. Falling back then would mount
  // the dashboard — fetching its data — and swap it out a moment later, which
  // reads as a flash and wastes the round trip. The fallback therefore waits
  // until the roles are actually known.
  const rolesKnown = currentUser !== null;
  const shownTab = (!rolesKnown || navItems.some((i) => i.id === activeTab))
    ? activeTab
    : 'dashboard';

  // The tabs everyone gets. A tab outside this set cannot render until the
  // roles arrive, which is what the loader below covers.
  const OPEN_TABS = ['dashboard', 'new', 'my-requisitions', 'my-inbox', 'department'];
  const awaitingRoles = !rolesKnown && !OPEN_TABS.includes(shownTab);

  return (
    <div className="fade-in" style={{ padding: '0px 0px 24px', marginTop: -28 }}>
      {/* Navigation Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, paddingTop: 16, paddingBottom: 8, alignItems: 'stretch' }}>
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => goToTab(item.id)}
              style={{
                flex: 1,
                padding: '12px 8px',
                background: shownTab === item.id ? '#0b318bff' : 'var(--bg-card)',
                color: shownTab === item.id ? '#ffffff' : 'var(--text-primary)',
                border: '1px solid black',
                borderRadius: 12,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                textAlign: 'center',
                boxShadow: shownTab === item.id ? '0 4px 12px rgba(11,49,139,0.2)' : 'var(--shadow-sm)',
                transition: 'all 0.2s ease',
                whiteSpace: 'normal',
                lineHeight: 1.25,
              }}
            >
              {item.label}
            </button>
          ))}
      </div>

      {/* Content Area */}
      <div style={{ background: 'var(--bg-card)', border: '3px solid black', borderRadius: 16, minHeight: 400, padding: 24, boxShadow: 'var(--shadow-sm)' }}>
        {/* Arriving on a role-gated tab — the edit page's "Back to Approved
            Requisitions", for instance — every `canSee…` is still false until
            /me answers, so each branch below evaluates to nothing and the
            panel sat blank with no sign that anything was happening. It now
            says it is loading until the roles decide what belongs here. */}
        {awaitingRoles && <RNLLoader title="Loading..." />}
        {shownTab === 'dashboard' && <ERMSDashboard onViewAll={() => goToTab('my-requisitions')} />}
        {shownTab === 'new' && (
          <NewRequisition
            onSaveSuccess={() => goToTab('my-requisitions')}
            // /new -> the hub, /new/<dept> -> that form, /new/<dept>/drafts -> its drafts.
            deptSlug={subPath[0] || null}
            subView={FORM_VIEWS.includes(subPath[1]) ? subPath[1] : null}
            onNavigate={(slug, view) => navigate(
              ['/enterprise-requisition/new', slug, view].filter(Boolean).join('/')
            )}
          />
        )}
        {shownTab === 'my-requisitions' && (
          <MyRequisitions
            openPrId={opened.id}
            openForEdit={opened.edit}
            onOpenPr={prNav('/enterprise-requisition/my-requisitions')}
          />
        )}
        {shownTab === 'my-inbox' && <MyInbox />}
        {shownTab === 'department-head-review' && canSeeDepartmentHeadReview && <MyInbox stepNameFilter="Department Head Review" title="Department Head Review" />}
        {shownTab === 'cc-queue' && canSeeCcQueue && (
          <MyRequisitions
            queueStep="cc-queue"
            // An unrecognised workspace slug resolves to the picker rather than
            // an empty table — a mistyped or stale link should look like the
            // front door, not like a workspace with nothing in it.
            ccView={CC_WORKSPACES.includes(subPath[0]) ? subPath[0] : null}
            openPrId={opened.id}
            openForEdit={opened.edit}
            onOpenPr={prNav(`/enterprise-requisition/cc-queue/${subPath[0] || ''}`.replace(/\/$/, ''))}
            onCcViewChange={(v) => navigate(
              v ? `/enterprise-requisition/cc-queue/${v}` : '/enterprise-requisition/cc-queue'
            )}
          />
        )}
        {shownTab === 'approval-queue' && canSeeApprovalQueue && (
          <MyRequisitions
            queueStep="approval-queue"
            openPrId={opened.id}
            openForEdit={opened.edit}
            onOpenPr={prNav('/enterprise-requisition/approval-queue')}
          />
        )}
        {shownTab === 'approved-requisitions' && canSeeApprovalQueue && (
          <ApprovalOutcomeRequisitions
            mode="approved"
            openPrId={opened.id}
            onOpenPr={prNav('/enterprise-requisition/approved-requisitions')}
          />
        )}
        {shownTab === 'rejected-returned-requisitions' && canSeeApprovalQueue && (
          <ApprovalOutcomeRequisitions
            mode="rejected-returned"
            openPrId={opened.id}
            onOpenPr={prNav('/enterprise-requisition/rejected-returned-requisitions')}
          />
        )}
        {shownTab === 'department' && canSeeDepartmentRequisitions && (
          <DepartmentRequisitions
            openPrId={opened.id}
            onOpenPr={prNav('/enterprise-requisition/department')}
          />
        )}
      </div>
    </div>
  );
}
